import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

const DiscussionCard = ({ discussion }) => {
	const theme = useTheme();
	const styles = useStyles();

	const lastActiveDate = new Date(discussion.lastActive);
	const timeAgo = new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(Math.ceil((lastActiveDate - new Date()) / (1000 * 60 * 60 * 24)), "day");

	// Calculate unread messages (in a real app, this would come from the server)
	const unreadMessages = discussion.messages - (discussion.lastReadCount || 0);

	return (
		<TouchableOpacity style={[styles.discussionCard, discussion.pinned && styles.pinnedCard]}>
			{discussion.pinned && (
				<View style={styles.pinnedBadge}>
					<Ionicons name="pin" size={14} color={theme.colors.textSecondary} />
					<Text style={styles.pinnedText}>Pinned</Text>
				</View>
			)}

			<Text style={styles.discussionTitle}>{discussion.title}</Text>
			<Text style={styles.discussionDescription}>{discussion.description}</Text>

			<View style={styles.discussionStats}>
				<View style={styles.statItem}>
					<Ionicons name="people-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.statText}>{discussion.participants}</Text>
				</View>
				<View style={styles.statItem}>
					<Ionicons name="chatbubble-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.statText}>{discussion.messages}</Text>
				</View>
				<View style={styles.statItem}>
					<Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.statText}>Active {timeAgo}</Text>
				</View>
			</View>

			<View style={styles.tags}>
				{discussion.tags.map((tag, index) => (
					<View key={index} style={styles.tag}>
						<Text style={styles.tagText}>{tag}</Text>
					</View>
				))}
			</View>

			<View style={styles.discussionActions}>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="bookmark-outline" size={20} color={theme.colors.textSecondary} />
				</TouchableOpacity>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="share-outline" size={20} color={theme.colors.textSecondary} />
				</TouchableOpacity>
				<TouchableOpacity style={styles.joinButton}>
					<Text style={styles.joinButtonText}>Join Discussion</Text>
					{unreadMessages > 0 && (
						<View style={styles.unreadBadge}>
							<Text style={styles.unreadCount}>{unreadMessages}</Text>
						</View>
					)}
					<Ionicons name="arrow-forward" size={16} color={theme.colors.systemBackground.primary} />
				</TouchableOpacity>
			</View>
		</TouchableOpacity>
	);
};

const Discussions = ({ discussions = [] }) => {
	const theme = useTheme();
	const styles = useStyles();
	const [searchQuery, setSearchQuery] = useState("");
	const [activeFilter, setActiveFilter] = useState("all");

	const filters = [
		{ id: "all", label: "All" },
		{ id: "pinned", label: "Pinned" },
		{ id: "unread", label: "Unread" },
		{ id: "active", label: "Most Active" },
	];

	const filteredDiscussions = discussions.filter((discussion) => {
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			return discussion.title.toLowerCase().includes(query) || discussion.description.toLowerCase().includes(query);
		}
		return true;
	});

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<View style={styles.searchBar}>
					<Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
					<TextInput style={styles.searchInput} placeholder="Search discussions..." placeholderTextColor={theme.colors.textSecondary} value={searchQuery} onChangeText={setSearchQuery} />
				</View>
				<TouchableOpacity style={styles.newDiscussionButton}>
					<Ionicons name="add" size={20} color={theme.colors.systemBackground.primary} />
					<Text style={styles.newDiscussionText}>New Discussion</Text>
				</TouchableOpacity>
			</View>

			<View style={styles.filters}>
				{filters.map((filter) => (
					<TouchableOpacity key={filter.id} style={[styles.filterButton, activeFilter === filter.id && styles.activeFilter]} onPress={() => setActiveFilter(filter.id)}>
						<Text style={[styles.filterText, activeFilter === filter.id && styles.activeFilterText]}>{filter.label}</Text>
					</TouchableOpacity>
				))}
			</View>

			<View style={styles.discussionList}>
				{filteredDiscussions.map((discussion) => (
					<DiscussionCard key={discussion.id} discussion={discussion} />
				))}
			</View>
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
		},
		header: {
			flexDirection: "row",
			gap: theme.spacing.md,
			marginBottom: theme.spacing.md,
		},
		searchBar: {
			flex: 1,
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.md,
			padding: theme.spacing.sm,
			...theme.shadows.sm,
		},
		searchIcon: {
			marginRight: theme.spacing.sm,
		},
		searchInput: {
			flex: 1,
			...theme.typography.body,
			color: theme.colors.text,
		},
		newDiscussionButton: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.md,
			paddingHorizontal: theme.spacing.md,
			gap: theme.spacing.sm,
		},
		newDiscussionText: {
			color: theme.colors.systemBackground.primary,
			...theme.typography.subhead,
			fontWeight: "600",
		},
		filters: {
			flexDirection: "row",
			gap: theme.spacing.sm,
			marginBottom: theme.spacing.md,
		},
		filterButton: {
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.sm,
			borderRadius: theme.borderRadius.pill,
			backgroundColor: theme.colors.systemBackground.primary,
		},
		activeFilter: {
			backgroundColor: theme.colors.primary,
		},
		filterText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
		},
		activeFilterText: {
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		discussionList: {
			gap: theme.spacing.md,
		},
		discussionCard: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.md,
			...theme.shadows.sm,
		},
		pinnedCard: {
			borderWidth: 1,
			borderColor: theme.colors.separator,
		},
		pinnedBadge: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
			marginBottom: theme.spacing.sm,
		},
		pinnedText: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
		},
		discussionTitle: {
			...theme.typography.headline,
			color: theme.colors.text,
			marginBottom: theme.spacing.sm,
		},
		discussionDescription: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.sm,
			lineHeight: 20,
		},
		discussionStats: {
			flexDirection: "row",
			gap: theme.spacing.md,
			marginBottom: theme.spacing.sm,
		},
		statItem: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.xs,
		},
		statText: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
		},
		tags: {
			flexDirection: "row",
			flexWrap: "wrap",
			gap: theme.spacing.sm,
			marginBottom: theme.spacing.md,
		},
		tag: {
			paddingVertical: theme.spacing.xs,
			paddingHorizontal: theme.spacing.sm,
			backgroundColor: theme.colors.gray[6],
			borderRadius: theme.borderRadius.pill,
		},
		tagText: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
		},
		discussionActions: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.sm,
		},
		actionButton: {
			padding: theme.spacing.sm,
		},
		joinButton: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.md,
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.md,
			marginLeft: "auto",
			gap: theme.spacing.sm,
		},
		joinButtonText: {
			...theme.typography.subhead,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		unreadBadge: {
			backgroundColor: theme.colors.error,
			borderRadius: theme.borderRadius.pill,
			paddingHorizontal: 6,
			paddingVertical: 2,
			minWidth: 20,
			alignItems: "center",
			marginRight: theme.spacing.xs,
		},
		unreadCount: {
			...theme.typography.caption2,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
	});
};

export default Discussions;
